import { Component, OnInit, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule } from '@angular/material/sort';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { catchError, map, Observable } from 'rxjs';
import { Client } from 'src/app/entities/client/client';
import { ClientRepository } from 'src/app/services/remote-data-service/repositories/person-repository/client.repository';
import { StoreService } from 'src/app/services/store-service/store.service';
import { CommonModule } from '@angular/common';
import { ClientsFormValues, FiltersService } from 'src/app/services/filters-service/filters.service';
import { OperationType } from 'src/app/models/operation-types';
import { Roles, UserRepository } from 'src/app/services/remote-data-service/repositories/user-repository/user.repository';
import { SnackbarService } from 'src/app/services/snackbar-service/snackbar.service';
import { ClientsMutationsComponent } from 'src/app/components/atoms/mutations/clients-mutations/clients-mutations.component';
import { EntityItemsAnnihilatorComponent } from 'src/app/components/atoms/mutations/entity-items-annihilator/entity-items-annihilator.component';

/**
 * Interface for grouping clients by their first letter
 * Used for displaying clients in an alphabetically organized list
 */
interface OrderedClients {
  letter: string;
  clients: Client[]
}

@Component({
  selector: 'app-clients-page',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatTableModule,
    MatSortModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule
  ],
  templateUrl: './clients-page.component.html',
  styleUrl: './clients-page.component.scss'
})
/**
 * Main component for managing clients. Provides functionality for:
 * - Displaying clients in an alphabetically ordered list
 * - Filtering clients by name
 * - Creating, updating and deleting clients (for users with proper permissions)
 */
export class ClientsPageComponent implements OnInit {
  // State management
  clients: Client[] = [];
  clients$: Observable<Client[]>;
  groupedClients: OrderedClients[] = [];

  public form = new FormGroup({
    name: new FormControl('', Validators.required),
  });

  isManager = false;

  readonly dialog = inject(MatDialog);

  constructor(
    protected store: StoreService,
    protected clientRep: ClientRepository,
    protected filters: FiltersService,
    protected userRep: UserRepository,
    protected snackbar: SnackbarService
  ) {
    this.clients$ = store.clients;

    this.clients$.pipe(
      catchError((err) => {
        console.error(err);
        return []
      }),
      map((clients) => {
        this.clients = clients;
        return clients;
      }),
      map((clients) => {
        this.orderClients(clients)
      })
    ).subscribe();
  }

  /**
   * Initializes the component by:
   * - Restoring any saved filter values
   * - Setting up form value change handlers
   * - Checking user permissions for management operations
   */
  ngOnInit(): void {
    this.filters.setClientsFilters({
      limit: 99999
    })

    if (this.filters.hasFormValues('clientsFilters')) {
      this.form.patchValue(
        this.filters.getFormValues<ClientsFormValues>('clientsFilters') || {}
      );
    }

    this.form.valueChanges.subscribe((changes) => {
      this.filters.saveFormValues('clientsFilters', changes);
      this.filters.setClientsFilters({
        name: changes.name || undefined,
      });
    });


    this.userRep.getCurrentUser().subscribe((user) => {
      this.isManager = user.roles.includes(Roles.chronos_manager)
    })
  }

  /**
   * Organizes clients into alphabetical groups for display
   * First sorts clients by name, then groups them by first letter
   */
  orderClients(clientsRes: Client[]) {
    const clients = clientsRes.sort((a, b) => a.name.localeCompare(b.name));

    // Group clients by first letter
    const grouped = clients.reduce((initial, client) => {
      const letter = client.name.charAt(0).toUpperCase();
      const group = initial.find(g => g.letter === letter);
      if (group) {
        group.clients.push(client);
      } else {
        initial.push({ letter: letter, clients: [client] });
      }
      return initial;
    }, [] as OrderedClients[]);

    this.groupedClients = grouped.sort((a, b) => a.letter.localeCompare(b.letter));
  }

  /**
   * Opens a dialog for creating a new client
   * Configures dialog dimensions and animation timings
   */
  openCreateClientDialog(
    enterAnimationDuration: string,
    exitAnimationDuration: string
  ): void {
    this.dialog.open(ClientsMutationsComponent, {
      width: 'auto',
      height: 'auto',
      maxWidth: '80dvw',
      maxHeight: '80dvh',
      enterAnimationDuration,
      exitAnimationDuration,
      data: {
        operation: OperationType.CREATE
      }
    });
  }

  /**
   * Opens a dialog for updating an existing client
   * Passes the client ID and configures dialog properties
   */
  openUpdateClientDialog(
    clientId: string,
    enterAnimationDuration: string,
    exitAnimationDuration: string
  ): void {
    this.dialog.open(ClientsMutationsComponent, {
      width: 'auto',
      height: 'auto',
      maxWidth: '80dvw',
      maxHeight: '80dvh',
      enterAnimationDuration,
      exitAnimationDuration,
      data: {
        operation: OperationType.UPDATE,
        id: clientId
      }
    });
  }

  /**
   * Removes a client from both backend and local store
   * Updates the grouped display after successful deletion
   */
  deleteClient(client: Client) {
    this.clientRep.removeClient(client).pipe(
      catchError(() => {
        return []
      })
    ).subscribe(() => {
      const clients = this.clients.filter(e => e !== client);
      if (clients) {
        this.clients = clients;
        this.snackbar.showSuccess('Deleted client')
        this.orderClients(clients);
      }
    })
  }


  openItemAnnihilatorDialog(
    enterAnimationDuration: string,
    exitAnimationDuration: string
  ): void {
    this.dialog.open(EntityItemsAnnihilatorComponent<Client>, {
      width: 'auto',
      height: 'auto',
      maxWidth: '80dvw',
      maxHeight: '80dvh',
      enterAnimationDuration,
      exitAnimationDuration,
      data: {
        entities: this.clients,
      }
    });
  }
}
